// REACT IMPORTS
import React, { useEffect, useState } from "react";
import Drawer from "components/ui-component/SideDrawer";
import Skeleton from "react-loading-skeleton";

// COMPONENT IMPORTS
import { useUserData } from "store/slices/authSlice";
import { Menu } from "@headlessui/react";
import {
  useSubscriptionCount,
  useSubscriptionHistory,
} from "store/slices/subscriptionSlice";
import SelectAllDropDown from "components/form/SelectAllDropDown";
import CustomTable from "components/ui-component/CustomTable";
import {
  FilterAmountOptions,
  FilterDateOptions,
  PaymentFilterMethodOptions,
  SubscriptionsStatusOptions,
} from "utils/Options";
import {
  cardToShortname,
  changePaymentScheduleName,
  decode,
  encode,
  formatCurrency,
  getPaymentMethod,
} from "utils/HelperFunctions";
import Header from "components/layout/header";
import AmountFilter from "components/payments/AmountFilter";
import SubscriptionsPreview from "components/subscriptions/SubscriptionsPreview";

// ICONS IMPORTS
import { GreenTrueIcon } from "icons/payments/GreenTrueIcon";
import { MoreIcon } from "icons/payments/MoreIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { CheckCircleIcon } from "icons/subscription/CheckCircleIcon";
import { CloseSmRedIcon } from "icons/subscription/CloseSmRedIcon";
import { ReceiptEditIcon } from "icons/subscription/ReceiptEditIcon";
import { CloseRingIcon } from "icons/subscription/CloseRingIcon";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import DateFilter from "components/subscriptions/DateFilter";
import UpdatePaymentPlan from "components/dashboard/UpdatePaymentPlan";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { goToInvalid } from "utils/invalidRoute";
import { formatPaymentListDate } from "utils/DateFormat";
import UpdateSusbcribePlan from "components/subscribe/UpdateSusbcribePlan";
import CancelledCalendar from "icons/subscription/CancelledCalendar";
import { useMediaQuery } from "react-responsive";
import { SettingsValue } from "types/payments";
import { RootState } from "store/store";
import {
  SubscriptionAndPlansColumn,
  SubscriptionPlansRow,
} from "types/subscriptionAndPlans";
import PlanPreview from "components/dashboard/PlanPreview";
import ExcalimIcon from "icons/payments/ExclaimIcon";
import { RedCrossIcon } from "icons/payments/RedCrossIcon";
import { ClockYellowIcon } from "icons/payments/ClockYellowIcon";
import UpdatePaymentMethod from "components/subscribe/UpdatePaymentMethod";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { onSetShowPlanDrawer, useIsLogin } from "store/slices/commonSlice";
import { getExternalRedirectForClusterId } from "config/externalClusterRedirects";

/* ------------------------------------- SUBSCRIPTION AND PLAN ------------------------------------- */

const getStatusDetails = (status: string) => {
  switch (status) {
    case "active":
      return {
        label: "Active",
        Icon: <CheckCircleIcon />,
        className: "bg-[#1678F20D] text-blue text-sm",
      };
    case "cancelled":
    case "canceled":
      return {
        label: "Canceled",
        Icon: <CloseSmRedIcon />,
        className: "bg-[#FF00000D] text-red text-sm",
      };
    case "completed":
    case "ended":
      return {
        label: "Completed",
        Icon: <GreenTrueIcon />,
        className: "bg-[#23B3390D] text-green text-sm",
      };
    case "incomplete":
      return {
        label: "Incomplete",
        Icon: <ExcalimIcon />,
        className: "bg-[#4F576A0D] text-txtSecondary text-sm",
      };
    case "unpaid":
      return {
        label: "Unpaid",
        Icon: <RedCrossIcon />,
        className: "bg-[#FF00000D] text-red text-sm",
      };
    case "past_due":
      return {
        label: "Past Due",
        Icon: <ClockYellowIcon />,
        className: "bg-[#FDB6000D] text-yellow text-sm",
      };
    default:
      return {
        label: "",
        Icon: <></>,
        className: "",
      };
  }
};

const SubscriptionsAndPlans = () => {
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });
  const isLessThan920 = useMediaQuery({ query: "(max-width:920px)" });
  const isLessThan680 = useMediaQuery({ query: "(max-width:680px)" });

  const userData = useUserData();
  const subscriptionCount = useSubscriptionCount();
  const subscriptionHistory = useSubscriptionHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLogin = useIsLogin();

  const [selectedFilter, setSelectedFilter] = useState<
    "all" | "active" | "completed" | "cancelled"
  >(location.state && location.state.status ? "active" : "all");
  const [status, setStatus] = useState<any>(
    location.state && location.state.status ? ["Active"] : []
  );
  const [method, setMethod] = useState([]);
  const [amount, setAmount] = useState(undefined);
  const [dateInput1, setDateInput1] = useState(undefined);
  const [dateInput2, setDateInput2] = useState(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isPreviewId, setIsPreviewId] = useState<string>("");
  const [isNestedPreviewOpen, setIsNestedPreviewOpen] =
    useState<boolean>(false);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  const [dateFilter, setdateFilter] = useState<any>();

  const [updateSubscribePlan, setUpdateSubscribePlan] =
    useState<boolean>(false);
  const [updateSubscribePlanId, setUpdateSubscribePlanId] = useState<
    string | null
  >();
  const [updateSubscriptionMethod, setUpdateSubscriptionMethod] =
    useState<boolean>(false);
  const [updateSubscriptionMethodId, setUpdateSubscriptionMethodId] =
    useState<any>();

  const [updatePlanId, setUpdatePlanId] = useState<string>("");
  const [isUpdatePlanOpen, setIsUpdatePlanOpen] = useState<boolean>(false);
  const [handleDrawerSize, setHandleDrawerSize] = useState<number>(0);
  const [updateData, setUpdateData] = useState<any>();
  const [isUpdatePreview, setIsUpdatePreview] = useState<boolean>(false);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [isApiCall, setIsApiCall] = useState<boolean>(false);
  const { type, id } = useParams();
  const [filter, setFilter] = useState<any>({
    skip: 0,
    limit: 20,
    method: undefined,
    status: "",
    amount: undefined,
    startDate: undefined,
    endDate: undefined,
  });
  const { accountPermission } = useAppSelector(
    (state: RootState) => state.auth
  );

  const columns: SubscriptionAndPlansColumn[] = [
    {
      id: "creation_date",
      name: "Created Date",
      minWidth: "120px",
      cell: (row: SubscriptionPlansRow) => {
        return (
          <div>
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.updation_date ? (
              formatPaymentListDate(
                row?.updation_date,
                userData?.data?.accountData?.timezone,
                "short"
              )
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "plan",
      name: "Plan",
      minWidth: "200px",
      cell: (row: SubscriptionPlansRow) => {
        return (
          <div className="font-medium text-txtPrimary leading-normal text-sm">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.plan_id > 0 ? (
              "Payment Agreement"
            ) : row?.recurring_type === "schedule" ? (
              "Custom Plan"
            ) : (
              getSubscriptionName(row?.productSubscriptions)
            )}
          </div>
        );
      },
    },
    {
      id: "payment_method",
      name: "Payment Method",
      minWidth: "160px",
      cell: (row: SubscriptionPlansRow) => {
        let payment_collection_type = row?.payment_collection_type || "";
        return (
          <div>
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : (
              getPaymentMethod({
                card_type: row?.cc_type,
                method: row?.payment_method,
                digits: row?.cc_4digit,
                account_number: row?.cc_4digit,
                className: "!gap-[2px]",
                isHidden: false,
                payment_collection_type: payment_collection_type,
              }) || "--"
            )}
          </div>
        );
      },
    },
    {
      id: "last_payment_status",
      name: "Status",
      minWidth: "160px",
      cell: (row: SubscriptionPlansRow) => {
        let status =
          row?.recurring_type !== "subscription"
            ? row?.last_payment_status
              ? row?.last_payment_status?.toLowerCase()
              : ""
            : row?.sub_status
              ? row?.sub_status?.toLowerCase()
              : "active";
        let { label, Icon, className } = getStatusDetails(status);
        return (
          <div className="relative">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : status !== "" ? (
              <div className="flex items-center gap-[3px]">
                <div
                  className={`pointer-events-none flex items-center py-[3px] px-[10px] gap-[6px] rounded-md ${className}`}
                >
                  {Icon}
                  {label}
                </div>
                {row?.showIcon !== "false" && label !== "Canceled" && (
                  <CustomTooltip
                    maxwidth="264px"
                    place="top"
                    id={`Schedule-custom-${row.id}`}
                    content={`<div class="font-normal text-xs leading-[18px] w-[250px]" > One or more of your recent payments
                  have failed. Click here to view plan and retry. </div>`}
                    stopPropagation={true}
                  >
                    <div
                      className="w-fit cursor-pointer relative"
                      id={`Schedule-custom-${row.id}`}
                    >
                      <InfoRedCircleIcon />
                    </div>
                  </CustomTooltip>
                )}
                {row?.recurring_type === "subscription" &&
                  label === "Canceled" &&
                  row?.cancel_at_period_end === 1 && (
                    <CustomTooltip
                      maxwidth="264px"
                      place="top"
                      id={`Schedule-calendar-${row.id}`}
                      content={`<div class="font-normal text-xs leading-[18px] w-[180px]" > Your plan will be canceled on
                       ${formatPaymentListDate(
                         row?.next_billing_date,
                         userData?.data?.accountData?.timezone,
                         "long"
                       )}. </div>`}
                      stopPropagation={true}
                    >
                      <div
                        className="w-fit cursor-pointer relative"
                        id={`Schedule-calendar-${row.id}`}
                      >
                        <CancelledCalendar />
                      </div>
                    </CustomTooltip>
                  )}
              </div>
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "payment_schedule",
      name: "Frequency",
      minWidth: "110px",
      cell: (row: SubscriptionPlansRow) => {
        return (
          <div className="text-txtPrimary">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : (
              changePaymentScheduleName(row?.payment_schedule)
            )}
          </div>
        );
      },
    },
    {
      id: "next_billing_date",
      name: "Next Payment",
      minWidth: "130px",
      cell: (row: SubscriptionPlansRow) => {
        return (
          <div className="capitalize text-sm text-txtSecondary">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.sub_status === "CANCELLED" ? (
              "--"
            ) : row.next_billing_date ? (
              formatPaymentListDate(
                row.next_billing_date,
                userData?.data?.accountData?.timezone,
                "short"
              )
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "next_payment_amount",
      name: "Next Payment Amount",
      minWidth: "190px",
      cell: (row: SubscriptionPlansRow) => {
        return (
          <div className="capitalize text-sm text-txtSecondary">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.next_payment_amount ? (
              formatCurrency(
                row?.next_payment_amount + (row?.cf_amount || 0)
              ) || "$0.00"
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "id",
      name: "",
      right: "true",
      minWidth: "120px",
      cell: (row: SubscriptionPlansRow) => {
        const isAgreement = row?.plan_id && row?.plan_id > 0;

        const isUpdatePlan =
          row?.plan_id > 0 || row?.recurring_type === "schedule"
            ? accountPermission?.modify_plan === SettingsValue.YES &&
              !isAgreement
            : row?.productSubscriptions?.length === 1 &&
              row?.payment_collection_type === "MOF";

        let id = `${row?.id}`;

        return isTableLoading ? (
          <></>
        ) : (
          <div className="flex items-center gap-5">
            <div className="items-center gap-5 hidden icon-group">
              <div
                onClick={() => {
                  setIsPreview(true);
                  setIsPreviewId(id);
                }}
              >
                <EyeIcon
                  className="group"
                  _className="group-hover:!fill-iconObscure"
                />
              </div>
            </div>
            <Menu as="div" className="relative">
              <Menu.Button>
                <div className="hover:!bg-bgHover p-[9px] rounded-full">
                  <MoreIcon
                    className="group"
                    _className="group-hover:!fill-iconObscure"
                  />
                </div>
              </Menu.Button>

              <Menu.Items
                className={`absolute w-[153px] -left-[130px] top-[35px]
                    
                 !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg`}
              >
                <Menu.Item
                  as="div"
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px]"
                  onClick={() => {
                    setIsPreview(true);
                    setIsPreviewId(id);
                  }}
                >
                  <EyeIcon width="16" height="16" /> Preview
                </Menu.Item>

                {row?.last_payment_status?.toLowerCase() !== "cancelled" &&
                  row?.last_payment_status?.toLowerCase() !== "canceled" && (
                    <>
                      {isUpdatePlan &&
                        row?.last_payment_status !== "Completed" &&
                        row.sub_status !== "CANCELLED" && (
                          <Menu.Item
                            as="div"
                            className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                            // onClick={() => {
                            //   if (row?.recurring_type === "subscription") {
                            //     setUpdateSubscribePlan(true);
                            //     setUpdateSubscribePlanId(
                            //       encode(
                            //         `${id}_${clusterId}_${new Date().getTime()}`,
                            //         "sub_"
                            //       )
                            //     );
                            //   } else {
                            //     setIsUpdatePlanOpen(true);
                            //     setUpdatePlanId(
                            //       encode(
                            //         `${id}_${clusterId}_${new Date().getTime()}`,
                            //         "plan_"
                            //       )
                            //     );
                            //   }
                            // }}
                          >
                            <ReceiptEditIcon /> Update plan
                          </Menu.Item>
                        )}
                      {accountPermission?.cancel_plan === SettingsValue.YES &&
                        row?.last_payment_status !== "Completed" &&
                        row.sub_status !== "CANCELLED" && (
                          <Menu.Item
                            as="div"
                            className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px] border-t border-strokePrimary"
                            // onClick={() => {
                            //   if (row?.recurring_type === "subscription") {
                            //     setCancelSubscribePlan(true);
                            //     setCancelSubscribePlanId(id);
                            //   } else {
                            //     setCancelPlan({
                            //       id: row?.id,
                            //       showModal: true,
                            //     });
                            //   }
                            // }}
                          >
                            <CloseRingIcon width="16" height="16" /> Cancel plan
                          </Menu.Item>
                        )}
                    </>
                  )}
              </Menu.Items>
            </Menu>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (selectedFilter === "all" && !status?.length) return;

    switch (selectedFilter) {
      case "all":
        setStatus(["all"]);
        break;
      case "active":
        setStatus(["Active"]);
        break;
      case "completed":
        setStatus(["Completed"]);
        break;
      case "cancelled":
        setStatus(["Canceled"]);
        break;
      default:
        setStatus([]);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isSubscriptionDrawer = searchParams.get("subsDrawer") === "true";
    const subscriptionId = searchParams.get("subscriptionId") || id;

    if (
      (isSubscriptionDrawer || (Boolean(type) && Boolean(id))) &&
      subscriptionId
    ) {
      if (type === "subscription-method") {
        setUpdateSubscriptionMethod(true);
        setUpdateSubscriptionMethodId(subscriptionId);
      } else {
        setUpdateSubscribePlan(true);
        setUpdateSubscribePlanId(subscriptionId);
      }
    } else if (
      searchParams.has("plansDrawer") &&
      searchParams.get("plansDrawer") === "true" &&
      searchParams.has("planId")
    ) {
      setIsUpdatePlanOpen(true);
      let id = searchParams.get("planId") ?? "";
      setUpdatePlanId(id);
    }
  }, [id, location.search, type]);

  useEffect(() => {
    const methodFormatted = method?.map((data) => cardToShortname(data));
    if (!status?.length) {
      setSelectedFilter("all");
    }
    setFilter(
      dateFilter
        ? {
            skip: 0,
            limit: 20,
            status:
              status.length > 0
                ? JSON.stringify(
                    status.map((item: any) =>
                      item === "Canceled" ? "Cancelled" : item
                    )
                  )
                : undefined,
            dateFilter: dateFilter,
          }
        : {
            ...filter,
            skip: 0,
            method:
              method.length > 0 ? JSON.stringify(methodFormatted) : undefined,
            status:
              status.length > 0
                ? status.map((item: any) =>
                    item === "Canceled" ? "Cancelled" : item
                  )
                : undefined,
            amount: amount ? JSON.stringify(amount) : undefined,
          }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, method, amount, dateFilter]);

  const getSubscriptionName = (productDetails: any) => {
    if (productDetails?.length > 0) {
      let title = productDetails?.[0]?.productDetails?.name || "";
      if (productDetails?.length > 1) {
        return (
          title?.charAt(0) +
          title?.slice(1) +
          ` and ${productDetails.length - 1} more`
        );
      } else {
        return title?.charAt(0) + title?.slice(1);
      }
    } else {
      return "Custom Plan";
    }
  };

  useEffect(() => {
    if (type && id) {
      const decodedSlug = decode(id);
      const slugArray = decodedSlug?.split("_");
      const clusterIdFromSlug = slugArray?.[1] || "";
      dispatch(
        onSetShowPlanDrawer({
          drawerType: type,
          id,
        })
      );
      if (!clusterIdFromSlug) {
        goToInvalid(navigate, "link");
        return;
      }
      const externalRedirect =
        getExternalRedirectForClusterId(clusterIdFromSlug);
      if (!isLogin && externalRedirect) {
        // Skip the login hop: this cluster's login lives on another site.
        window.location.replace(externalRedirect);
        return;
      }
      const clusterSlug = encode(clusterIdFromSlug, "ci_");
      if (isLogin) {
        navigate(`/${clusterSlug}/subscriptions-plans`);
      } else {
        navigate(`/${clusterSlug}/login`);
      }
    }
  }, [type, id]);

  const GetRow = (dataRow?: any) => {
    let status =
      dataRow?.recurring_type !== "subscription"
        ? dataRow?.last_payment_status
          ? dataRow?.last_payment_status?.toLowerCase()
          : ""
        : dataRow?.sub_status
          ? dataRow?.sub_status?.toLowerCase()
          : "active";
    let { label, Icon, className } = getStatusDetails(status);

    return (
      <div className="w-full py-3 flex flex-col items-start justify-start gap-[10px] relative">
        <div className="flex justify-between items-center w-full">
          <span className="text-xl font-medium">
            {formatCurrency(dataRow?.amount) || "$0.00"}
          </span>
          <span className="flex items-center justify-end gap-2">
            <div
              className={`pointer-events-none flex items-center py-[3px] px-[10px] gap-[6px] rounded-md capitalize ${className}`}
            >
              {Icon}
              {label}
            </div>

            {dataRow?.showIcon !== "false" && (
              <>
                <CustomTooltip
                  maxwidth="264px"
                  place="top"
                  id={`Schedule-custom-${dataRow.id}`}
                  content={`<div class="font-normal text-xs leading-[18px] w-[250px]" > One or more of your recent payments
                  have failed. Click here to view plan and retry. </div>`}
                  stopPropagation={true}
                >
                  <div
                    className="w-fit cursor-pointer relative"
                    id={`Schedule-custom-${dataRow.id}`}
                  >
                    <InfoRedCircleIcon />
                  </div>
                </CustomTooltip>
              </>
            )}
            {dataRow?.recurring_type === "subscription" &&
              label === "Canceled" &&
              dataRow?.cancel_at_period_end === 1 && (
                <CustomTooltip
                  maxwidth="264px"
                  place="top"
                  id={`Schedule-calendar-${dataRow?.id}`}
                  content={`<div class="font-normal text-xs leading-[18px] w-[180px]" > Your plan will be canceled on
                       ${formatPaymentListDate(
                         dataRow?.next_billing_date,
                         userData?.data?.accountData?.timezone,
                         "long"
                       )}. </div>`}
                  stopPropagation={true}
                >
                  <div
                    className="w-fit cursor-pointer relative"
                    id={`Schedule-calendar-${dataRow?.id}`}
                  >
                    <CancelledCalendar />
                  </div>
                </CustomTooltip>
              )}
            <Menu as="div" className="relative grid place-items-center">
              <Menu.Button onClick={(e) => e.stopPropagation()}>
                <MoreIcon
                  className="group"
                  _className="group-hover:!fill-iconObscure"
                />
              </Menu.Button>

              <Menu.Items
                className={`absolute w-[153px] -left-[130px] top-[35px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg`}
              >
                <Menu.Item
                  as="div"
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px]"
                  onClick={() => {
                    setIsPreview(true);
                    setIsPreviewId(dataRow?.id);
                  }}
                >
                  <EyeIcon width="16" height="16" /> Preview
                </Menu.Item>

                {dataRow?.last_payment_status?.toLowerCase() !== "cancelled" &&
                  dataRow?.last_payment_status?.toLowerCase() !==
                    "canceled" && (
                    <>
                      {accountPermission?.modify_plan === SettingsValue.YES && (
                        <Menu.Item
                          as="div"
                          className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                          onClick={() => {
                            if (dataRow?.recurring_type === "subscription") {
                              setUpdateSubscribePlan(true);
                              setUpdateSubscribePlanId(dataRow.id);
                            } else {
                              setIsUpdatePlanOpen(true);
                              setUpdatePlanId(dataRow.id);
                            }
                          }}
                        >
                          <ReceiptEditIcon /> Update plan
                        </Menu.Item>
                      )}
                      {accountPermission?.cancel_plan === SettingsValue.YES && (
                        <Menu.Item
                          as="div"
                          className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px] border-t border-strokePrimary"
                          // onClick={() => {
                          //   if (dataRow?.recurring_type === "subscription") {
                          //     setCancelSubscribePlan(true);
                          //     setCancelSubscribePlanId(dataRow?.id);
                          //   } else {
                          //     setCancelPlan({
                          //       id: dataRow?.id,
                          //       showModal: true,
                          //     });
                          //   }
                          // }}
                        >
                          <CloseRingIcon width="16" height="16" /> Cancel plan
                        </Menu.Item>
                      )}
                    </>
                  )}
              </Menu.Items>
            </Menu>
          </span>
        </div>
        <div className="w-[85%] flex flex-col gap-[10px]">
          <div className="w-full flex justify-between items-center text-txtParagraph text-sm">
            <span className="w-[45%]">Payment method</span>
            <span className="w-[50%]">
              {getPaymentMethod({
                card_type: dataRow?.cc_type,
                method: dataRow?.payment_method,
                digits: dataRow?.cc_4digit,
                account_number: dataRow?.cc_4digit,
                className: "!gap-[2px]",
                textClassName: "",
                isHidden: false,
              }) || "--"}
            </span>
          </div>
          <div className="w-full flex justify-between items-center text-txtParagraph text-sm">
            <span className="w-[45%]">Created date</span>
            <span className="w-[50%]">
              {formatPaymentListDate(
                dataRow.updation_date,
                userData?.data?.accountData?.timezone,
                "short"
              )}
            </span>
          </div>
          <div className="w-full flex justify-between items-center text-txtParagraph text-sm">
            <span className="w-[45%]">Plan</span>
            <span className="w-[50%]">
              {dataRow?.plan_id > 0
                ? "Payment Agreement"
                : dataRow?.recurring_type === "schedule"
                  ? "Custom Plan"
                  : getSubscriptionName(dataRow?.productSubscriptions)}
            </span>
          </div>
          <div className="w-full flex justify-between items-center text-txtParagraph text-sm capitalize">
            <span className="w-[45%]">Frequency</span>
            <span className="w-[50%]">
              {changePaymentScheduleName(dataRow?.payment_schedule)}
            </span>
          </div>
          <div className="w-full flex justify-between items-center text-txtParagraph text-sm">
            <span className="w-[45%]">Next Payment</span>
            <span className="w-[50%]">
              {dataRow?.next_billing_date
                ? formatPaymentListDate(
                    dataRow.next_billing_date,
                    userData?.data?.accountData?.timezone,
                    "short"
                  )
                : "--"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="w-full h-full lg:block flex flex-col">
        <Header
          headerContent={
            <div className="lg:text-[32px] sm:text-3xl text-2xl leading-[44px] text-txtPrimary font-medium -tracking-[0.72px] lg:p-0 p-5 h-fit">
              Subscriptions & Plans
            </div>
          }
        />
        <div className="lg:pt-[93px] h-full overflow-y-auto">
          <div className="lg:px-10 lg:pt-10 h-full overflow-y-auto">
            <div className="lg:px-0 px-5 flex items-center lg:gap-[28px] lg:flex-nowrap flex-wrap gap-5 lg:overflow-x-auto">
              <div
                className={`${
                  selectedFilter === "all"
                    ? "border-blue"
                    : "border-strokePrimary"
                } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start lg:min-w-40`}
                // onClick={() => {
                //   setSelectedFilter("all");
                //   setStatus([]);
                // }}
              >
                <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                  All
                </div>
                <div className="text-lg leading-normal text-txtSecondary pt-[14px] animate__animated animate__fadeIn">
                  {isLoading ? (
                    <Skeleton width={27} height={23} />
                  ) : (
                    subscriptionCount?.All || 0
                  )}
                </div>
              </div>
              <div
                className={`${
                  selectedFilter === "active"
                    ? "border-blue"
                    : "border-strokePrimary"
                } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start lg:min-w-40`}
                // onClick={() => {
                //   setSelectedFilter("active");
                //   // setStatus([]);
                // }}
              >
                <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                  Active
                </div>
                <div className="text-lg leading-normal text-txtSecondary pt-[14px] animate__animated animate__fadeIn">
                  {isLoading ? (
                    <Skeleton width={27} height={23} />
                  ) : (
                    subscriptionCount?.Active || 0
                  )}
                </div>
              </div>
              <div
                className={`${
                  selectedFilter === "completed"
                    ? "border-blue"
                    : "border-strokePrimary"
                } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start lg:min-w-40`}
                // onClick={() => {
                //   setSelectedFilter("completed");
                // }}
              >
                <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                  Completed
                </div>
                <div className="text-lg leading-normal text-txtSecondary pt-[14px] animate__animated animate__fadeIn">
                  {isLoading ? (
                    <Skeleton width={27} height={23} />
                  ) : (
                    subscriptionCount?.Completed || 0
                  )}
                </div>
              </div>
              <div
                className={`${
                  selectedFilter === "cancelled"
                    ? "border-blue"
                    : "border-strokePrimary"
                } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start lg:min-w-40`}
                // onClick={() => {
                //   setSelectedFilter("cancelled");
                // }}
              >
                <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                  Canceled
                </div>
                <div className="text-lg leading-normal text-txtSecondary pt-[14px] animate__animated animate__fadeIn">
                  {isLoading ? (
                    <Skeleton width={27} height={23} />
                  ) : (
                    subscriptionCount?.Cancelled || 0
                  )}
                </div>
              </div>
            </div>
            <div className="lg:px-0 px-5 pt-[30px] flex items-center gap-3 lg:flex-nowrap flex-wrap">
              <div className="lg:w-fit sm:w-[48%] w-full">
                <DateFilter
                  options={FilterDateOptions}
                  dateInput1={dateInput1}
                  dateInput2={dateInput2}
                  setDateInput1={setDateInput1}
                  setDateInput2={setDateInput2}
                  label="Date"
                  setdateFilter={setdateFilter}
                />
              </div>
              <div className="lg:w-fit sm:w-[48%] w-full">
                <AmountFilter
                  options={FilterAmountOptions}
                  amount={amount}
                  setAmount={setAmount}
                  label="Amount"
                />
              </div>
              <div className="lg:w-fit sm:w-[48%] w-full">
                <SelectAllDropDown
                  options={SubscriptionsStatusOptions}
                  checked={status}
                  setChecked={setStatus}
                  label="Status"
                />
              </div>
              <div className="lg:w-fit sm:w-[48%] w-full">
                <SelectAllDropDown
                  options={PaymentFilterMethodOptions}
                  checked={method}
                  setChecked={setMethod}
                  label="Payment Method"
                />
              </div>
            </div>
            <div className="pt-[30px]">
              <CustomTable
                columns={columns}
                data={subscriptionHistory?.paymentPlanHistoryData || []}
                fixedHeaderScrollHeight="calc(var(--shell-vh) - 452px)"
                perPage={filter?.limit}
                currentPage={(filter?.skip + 20) / 20}
                totalCount={subscriptionHistory?.totalCount || 0}
                isTableLoading={isTableLoading}
                onNextClick={(e) => {
                  setFilter({
                    ...filter,
                    skip: filter?.skip + 20,
                  });
                }}
                onPreviousClick={(e) => {
                  setFilter({
                    ...filter,
                    skip: filter?.skip - 20,
                  });
                }}
                GetRow={GetRow}
                onRowClick={(row: any, e: any) => {
                  setIsPreview(true);
                  setIsPreviewId(row?.id);
                }}
              />
            </div>
            <div>
              <Drawer
                enableOverlay={true}
                lockBackgroundScroll={true}
                direction="right"
                open={isPreview}
                size={isLessThan590 ? `100vw` : "590px"}
                onClose={() => {
                  if (isNestedPreviewOpen) return;
                  setIsPreview(false);
                  setIsPreviewId("");
                }}
                overlayColor="#000"
              >
                <SubscriptionsPreview
                  id={Number(isPreviewId)}
                  setId={setIsPreviewId}
                  show={isPreview}
                  setShow={setIsPreview}
                  setHandleDrawerSize={setHandleDrawerSize}
                  onNestedPreviewChange={setIsNestedPreviewOpen}
                />
              </Drawer>
            </div>
          </div>
        </div>
      </div>

      {/* Plan preview */}
      <div>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={isUpdatePreview}
          size={isLessThan590 ? `100vw` : "590px"}
          onClose={() => {
            setIsUpdatePreview(false);
            setHandleDrawerSize(0);
          }}
          overlayColor="#000"
        >
          <PlanPreview
            show={isUpdatePreview}
            setShow={setIsUpdatePreview}
            schedule={updateData?.paymentPlan}
            isLoading={isLoading}
            scheduleMethod={selectedMethod}
            setHandleDrawerSize={setHandleDrawerSize}
          />
        </Drawer>
      </div>

      {/* Update Payment Plan */}
      <div>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={isUpdatePlanOpen}
          size={`${handleDrawerSize ? "0" : isLessThan920 ? `100vw` : "920px"}`}
          onClose={() => {
            setIsUpdatePreview(false);
          }}
          overlayColor="#000"
        >
          <UpdatePaymentPlan
            id={updatePlanId}
            show={isUpdatePlanOpen}
            setShow={setIsUpdatePlanOpen}
            setUpdatePlanId={(value?: string) => {
              setUpdatePlanId(value || "");
            }}
            setSelectedMethod={setSelectedMethod}
            setIsPreview={setIsUpdatePreview}
            setHandleDrawerSize={setHandleDrawerSize}
            setUpdateData={setUpdateData}
            isApiCall={isApiCall}
            setIsApiCall={setIsApiCall}
            setFinalPreview={setIsPreview}
            setFinalPreviewId={(value?: string) => {
              setIsPreviewId(value || "");
            }}
            isFinalPreview={isPreview}
          />
        </Drawer>
      </div>

      {/* Update Subscribe Plan */}
      <div>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={updateSubscribePlan}
          size={`${handleDrawerSize ? "0" : isLessThan680 ? `100vw` : "680px"}`}
          onClose={() => {
            setUpdateSubscribePlan(false);
            setUpdateSubscribePlanId(null);
            setHandleDrawerSize(0);
          }}
          overlayColor="#000"
        >
          <UpdateSusbcribePlan
            id={updateSubscribePlanId}
            show={updateSubscribePlan}
            setShow={setUpdateSubscribePlan}
            setId={setUpdateSubscribePlanId}
            fetchDetails={() => {}}
            setHandleDrawerSize={setHandleDrawerSize}
            setIsPreview={setIsPreview}
            setIsPreviewId={setIsPreviewId}
          />
        </Drawer>
      </div>

      {/* Update Payment Method */}
      <div>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={updateSubscriptionMethod}
          size={`${handleDrawerSize ? "0" : isLessThan680 ? `100vw` : "680px"}`}
          onClose={() => {
            setUpdateSubscriptionMethod(false);
            setUpdateSubscriptionMethodId(null);
          }}
          overlayColor="#000"
        >
          <UpdatePaymentMethod
            id={updateSubscriptionMethodId}
            show={updateSubscriptionMethod}
            setShow={setUpdateSubscriptionMethod}
            setId={setUpdateSubscriptionMethodId}
            fetchDetails={() => {}}
            setHandleDrawerSize={setHandleDrawerSize}
            setIsPreview={setIsPreview}
            setIsPreviewId={setIsPreviewId}
          />
        </Drawer>
      </div>
    </>
  );
};

export default SubscriptionsAndPlans;
